/**
 * QUEM É A EMPRESA DESTA ABA — e quem pode mudar isso.
 *
 * 🔴 O QUE ISTO CONSERTA (reproduzido em produção, 28/07 23h):
 *
 * `lib/api.ts` já explica, com todas as letras, que "a empresa é da ABA, não do
 * navegador": o header `X-Empresa-Id` sai de um holder que vive na memória desta
 * página, e o `localStorage` deveria ser só a memória de qual empresa ABRIR no
 * próximo boot. O `useEmpresaAtiva` chega a instalar um ouvinte de `storage` que
 * de propósito NÃO troca a empresa desta aba — o comentário lá diz que
 * recarregar aqui "jogaria fora o formulário que o dono tem aberto na tela".
 *
 * Só que a promessa valia até a próxima carga de página. `ativaId` nascia de
 * `localStorage.getItem(EMPRESA_STORAGE_KEY)` — uma chave GLOBAL à origem. Então
 * qualquer recarga (F5, service worker novo, ou o reload que o próprio painel
 * fabricava pra acertar a pele) fazia esta aba adotar em silêncio a empresa que
 * OUTRA aba escolheu. Provado ao vivo: uma aba mostrando "M5 Group · Painel" em
 * /socios voltou como "Vitrine — Obras · Painel" na carga seguinte, sem ninguém
 * escolher nada. Com um formulário aberto, o próximo salvamento escreveria no
 * tenant errado.
 *
 * A correção é a que o resto do arquivo já pedia: a aba CARIMBA a empresa dela
 * em `sessionStorage` (que é por aba, e sobrevive a recarga), e só consulta o
 * `localStorage` quando ainda não escolheu nada — aba nova continua abrindo na
 * última empresa usada, que é o comportamento que todo mundo espera.
 */
import { EMPRESA_STORAGE_KEY } from "./api";
import { gravarEmpresaDaPele } from "./skin";

/** A empresa DESTA aba. Por aba de propósito — ver o cabeçalho. */
export const EMPRESA_ABA_KEY = "m5_empresa_aba";

/**
 * Qual empresa esta aba deve abrir.
 *
 * Pura, pra ser testável por sabotagem: recebe os dois lados e devolve quem
 * ganha. A aba manda; o navegador só serve de sugestão pra aba que ainda não
 * escolheu.
 */
export function empresaDaAba(daAba: string | null, doNavegador: string | null): string | null {
  return daAba ?? doNavegador ?? null;
}

/** Lê a decisão desta aba + a sugestão do navegador, com storage bloqueado tolerado. */
export function empresaInicial(): string | null {
  let daAba: string | null = null;
  let doNavegador: string | null = null;
  try {
    daAba = sessionStorage.getItem(EMPRESA_ABA_KEY);
  } catch {
    // sessionStorage bloqueado — cai na sugestão do navegador (comportamento antigo)
  }
  try {
    doNavegador = localStorage.getItem(EMPRESA_STORAGE_KEY);
  } catch {
    // storage bloqueado — sem sugestão; o provider resolve pela lista
  }
  return empresaDaAba(daAba, doNavegador);
}

/**
 * A ÚNICA porta pra mudar de empresa. Grava as TRÊS coisas que precisam andar
 * juntas, nesta ordem:
 *
 *   1. a empresa DESTA aba          (sessionStorage) — pra a aba não ser sequestrada
 *   2. a empresa do PRÓXIMO boot    (localStorage)   — pra a próxima aba abrir certo
 *   3. o slug da PELE               (localStorage)   — pra o boot não decidir a pele
 *                                                      com informação velha e ter
 *                                                      que se corrigir com um reload
 *
 * O item 3 era o esquecido: `EmpresaSwitcher.irEmpresa` (vindo do Operador) e
 * `Operador.entrarComoSuporte` gravavam a empresa e navegavam pra "/" SEM gravar
 * o slug. O boot lia o slug da empresa ANTERIOR, escolhia a pele errada, e o
 * `useEmpresaAtiva` corrigia com `window.location.reload()` — um boot inteiro
 * jogado fora, medido em produção: 3 chamadas a /me/perfil e 2 a /me/empresas
 * para UM clique. Guardrail em `tests/boot-identidade-e-contexto.test.ts`.
 */
/**
 * O reload de correção da pele só pode acontecer UMA vez por aba.
 *
 * Marca antes de recarregar; se a marca já existe, recusa. O carimbo vive no
 * sessionStorage: sobrevive à recarga (é essa a graça) e morre com a aba, então
 * a próxima sessão de trabalho não fica sem o conserto. Storage indisponível
 * responde `false` — sem onde contar, não recarrega, porque um painel em laço de
 * recarga é indistinguível de um painel travado. Ver o uso em `useEmpresaAtiva`.
 *
 * Mora AQUI, e não no hook, por dois motivos: é a mesma família de estado (o que
 * é desta ABA), e assim o teste alcança a regra sem arrastar o `useAuth` junto —
 * que constrói o cliente Supabase no corpo do módulo e exige variável de
 * ambiente que o CI não tem.
 */
const RELOAD_PELE_KEY = "m5_reload_pele";
export function podeRecarregarPelaPele(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_PELE_KEY)) return false;
    sessionStorage.setItem(RELOAD_PELE_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

export function lembrarEmpresa(id: string, slug: string | null | undefined): void {
  try {
    sessionStorage.setItem(EMPRESA_ABA_KEY, id);
  } catch {
    // sessionStorage bloqueado — segue; a aba fica sujeita à sugestão global
  }
  try {
    localStorage.setItem(EMPRESA_STORAGE_KEY, id);
  } catch {
    // storage bloqueado — a escolha vale só nesta carga
  }
  gravarEmpresaDaPele(slug);
}
